class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.modal = this.closest('.search-modal') || this.closest('.search-inline');
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.querySelector('form.search').addEventListener('submit', this.onFormSubmit.bind(this));
    this.querySelector('button[type="button"]').addEventListener('click', this.close.bind(this));
    this.querySelector('button[type="reset"]').addEventListener('click', this.clear.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // Make the whole pill-shaped field clickable — clicks on the padding/
    // background should focus the input (buttons keep their own behaviour).
    const field = this.querySelector('.field');
    if (field) {
      field.addEventListener('click', (event) => {
        if (event.target.closest('button')) return;
        if (event.target === this.input) return;
        this.input.focus();
      });
      field.style.cursor = 'text';
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      if (this.closest('.search-inline')) {
        // Inline: keep the modal open, just hide stale results (cache stays
        // in DOM and CSS hides the panel via predictive-search:not([open]))
        this.removeAttribute('open');
        this.removeAttribute('results');
        this.removeAttribute('loading');
        if (this.modal) this.modal.classList.remove('searching');
      } else {
        this.clear();
        this.close(true);
      }
      return;
    }

    this.getSearchResults(this.searchTerm);
  }
  
  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const inline = this.closest('.search-inline');
    const mobileHost = this.closest('.header__search--mobile');

    if (!inline) {
      document.body.classList.add('predictive-search--focus');
    } else if (mobileHost && !mobileHost.classList.contains('is-searching')) {
      // Mobile: opening the full-page modal as soon as the input gains focus
      if (typeof setScrollbarWidth === 'function') setScrollbarWidth();
      document.body.classList.add('predictive-search--inline-open');
      // Portal to <body> so position:fixed actually covers the viewport
      // (sticky-header has a CSS transform which would clip a fixed child).
      if (!mobileHost._originalParent) {
        mobileHost._originalParent = mobileHost.parentNode;
        mobileHost._originalNextSibling = mobileHost.nextSibling;
      }
      document.body.appendChild(mobileHost);
      mobileHost.classList.add('is-searching');
      // Moving the element across the DOM can drop focus; re-apply it after
      // the next paint so the cursor lands cleanly inside the modal input.
      requestAnimationFrame(() => this.input.focus({ preventScroll: true }));
    }

    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.contains(document.activeElement)) return;
      const mobileHost = this.closest('.header__search--mobile');
      // While the mobile full-page modal is open, only the explicit X button
      // closes it — blur should not.
      if (mobileHost && mobileHost.classList.contains('is-searching')) return;
      this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      if (this.closest('.search-inline')) {
        // Inline: keep the modal open, just hide stale results
        this.removeAttribute('open');
        this.removeAttribute('results');
        this.removeAttribute('loading');
        if (this.modal) this.modal.classList.remove('searching');
      } else {
        this.clear(event);
        this.close(true);
      }
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
     
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      /*
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      */
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    
    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = ''; 

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
 
    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    const perPage = this.dataset.perPage || 4;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      if (this.modal) this.modal.classList.add('searching');
      return;
    }

    fetch(`${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[limit]')}=${perPage}&${encodeURIComponent('resources[limit_scope]')}=each&section_id=predictive-search`)
      .then((response) => { 
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;

        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );
        /*
        this.cachedResults[queryKey] = resultsMarkup;
        */
        this.renderSearchResults(resultsMarkup);
        
        if (this.modal) this.modal.classList.add('searching');
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        
        this.close();
        throw error;
      }); 
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);  

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() { 
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }
  
  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.querySelector('.header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    const inline = this.closest('.search-inline');
    if (!inline) {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    } else {
      this.predictiveSearchResults.style.maxHeight = '';
    }
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    document.body.classList.add('predictive-search--focus');
    if (inline && !document.body.classList.contains('predictive-search--inline-open')) {
      if (typeof setScrollbarWidth === 'function') setScrollbarWidth();
      if (typeof setHeaderBottomPosition === 'function') setHeaderBottomPosition();
      document.body.classList.add('predictive-search--inline-open');
      const mobileHost = this.closest('.header__search--mobile');
      if (mobileHost) {
        // Portal to <body> so position:fixed actually covers the viewport
        // (sticky-header has a CSS transform which would clip a fixed child).
        if (!mobileHost._originalParent) {
          mobileHost._originalParent = mobileHost.parentNode;
          mobileHost._originalNextSibling = mobileHost.nextSibling;
        }
        document.body.appendChild(mobileHost);
        mobileHost.classList.add('is-searching');
      }
    }
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    document.body.classList.remove('predictive-search--focus');
    document.body.classList.remove('predictive-search--inline-open');
    const mobileHost = this.closest('.header__search--mobile');
    if (mobileHost) {
      mobileHost.classList.remove('is-searching');
      // Restore to original DOM position so it sits back under the header
      if (mobileHost._originalParent) {
        if (mobileHost._originalNextSibling && mobileHost._originalNextSibling.parentNode === mobileHost._originalParent) {
          mobileHost._originalParent.insertBefore(mobileHost, mobileHost._originalNextSibling);
        } else {
          mobileHost._originalParent.appendChild(mobileHost);
        }
        mobileHost._originalParent = null;
        mobileHost._originalNextSibling = null;
      }
    }
    if (this.modal && this.modal.classList.contains('search-inline')) {
      this.modal.classList.remove('searching');
    }
  }

  clear(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Mobile X behavior depends on context:
    //  - If the full-page modal is open → just close the modal, keep the
    //    inline bar visible on the page (cached state preserved).
    //  - If the user is on the inline bar (modal closed) AND the input is
    //    empty → hide the bar (Snocks-style). Tap the header search icon
    //    to bring it back.
    const mobileHost = this.closest('.header__search--mobile');
    if (mobileHost) {
      const wasInModal = mobileHost.classList.contains('is-searching');
      if (wasInModal) {
        // X in modal: close without clearing (preserves cached state)
        this.close();
      } else {
        // X on inline bar: clear the text + close any residual state
        this.searchTerm = '';
        this.close(true);
      }
      return;
    }

    this.searchTerm = '';
    this.close(true);
    this.input.focus();
  }
}

customElements.define('predictive-search', PredictiveSearch);
